import React, { useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
} from 'react-flow-renderer';

import TriggerNode from './components/nodes/TriggerNode';
import LogicNode from './components/nodes/LogicNode';
import AINode from './components/nodes/AINode';
import ActionNode from './components/nodes/ActionNode';
import useFlowStore from '../../stores/flowStore';

const nodeTypes = {
  trigger: TriggerNode,
  logic: LogicNode,
  ai: AINode,
  action: ActionNode,
};

const FlowCanvas = () => {
  const {
    nodes: storeNodes,
    edges: storeEdges,
    setNodes,
    setEdges,
    setSelectedNode,
    deleteNode,
  } = useFlowStore();

  const [nodes, setNodesState, onNodesChange] = useNodesState(storeNodes);
  const [edges, setEdgesState, onEdgesChange] = useEdgesState(storeEdges);
  const reactFlowWrapper = useRef(null);

  // Sync store → local when store changes externally (load, delete, duplicate)
  useEffect(() => {
    setNodesState(storeNodes);
  }, [storeNodes, setNodesState]);

  useEffect(() => {
    setEdgesState(storeEdges);
  }, [storeEdges, setEdgesState]);

  // Sync local → store when local changes (drag, move, connect)
  useEffect(() => {
    setNodes(nodes);
  }, [nodes, setNodes]);

  useEffect(() => {
    setEdges(edges);
  }, [edges, setEdges]);

  const onConnect = useCallback(
    (params) => {
      const styledParams = {
        ...params,
        animated: true,
        style: { stroke: '#10aa62', strokeWidth: 2 },
      };
      setEdgesState((eds) => addEdge(styledParams, eds));
    },
    [setEdgesState]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      let nodeData;
      try {
        nodeData = JSON.parse(event.dataTransfer.getData('application/reactflow'));
      } catch {
        return; // Ignore invalid drops
      }

      const bounds = reactFlowWrapper.current
        ? reactFlowWrapper.current.getBoundingClientRect()
        : event.target.getBoundingClientRect();

      const position = {
        x: event.clientX - bounds.left - 100,
        y: event.clientY - bounds.top - 20,
      };

      const newNode = {
        id: `node-${Date.now()}`,
        type: nodeData.type || 'action',
        position,
        data: {
          label: nodeData.label,
          subtype: nodeData.subtype,
          config: nodeData.config || {},
        },
      };

      setNodesState((nds) => nds.concat(newNode));
    },
    [setNodesState]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onNodeClick = useCallback(
    (_event, node) => {
      setSelectedNode(node);
    },
    [setSelectedNode]
  );

  const onNodeDoubleClick = useCallback(
    (_event, node) => {
      setSelectedNode(node);
    },
    [setSelectedNode]
  );

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, [setSelectedNode]);

  // Delete selected nodes with Delete/Backspace keys
  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        const selected = nodes.filter((n) => n.selected);
        selected.forEach((n) => deleteNode(n.id));
      }
    },
    [nodes, deleteNode]
  );

  return (
    <div
      ref={reactFlowWrapper}
      style={{ width: '100%', height: '100%', minHeight: '600px', background: '#f9fafb' }}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        deleteKeyCode={null}
        style={{ background: '#f9fafb' }}
      >
        <Background color="#d1d5db" gap={16} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            switch (node.type) {
              case 'trigger': return '#10aa62';
              case 'ai': return '#a855f7';
              case 'logic': return '#0ea5e9';
              case 'action': return '#f97316';
              default: return '#6b7280';
            }
          }}
        />
      </ReactFlow>
    </div>
  );
};

export default FlowCanvas;
